import { useCallback } from 'react';

export enum ZaIdErrorCode {
  INVALID_FORMAT = 'invalid_format',
  INVALID_CHECKSUM = 'invalid_checksum',
  BIRTHDATE_MISMATCH = 'birthdate_mismatch',
  GENDER_MISMATCH = 'gender_mismatch',
}

// Standard Luhn checksum algorithm
// reference: https://github.com/MarjovanLier/SouthAfricanIDValidator/blob/main/src/SouthAfricanIDValidator.php
function isValidLuhn(number: string): boolean {
  if (!/^\d+$/.test(number)) return false;

  let total = 0;
  let doubleDigit = false;

  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number[i], 10);
    if (doubleDigit) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    total += digit;
    doubleDigit = !doubleDigit;
  }
  return total % 10 === 0;
}

const ISO_BIRTHDATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function formatBirthdate(date: string): string | null {
  if (!ISO_BIRTHDATE_REGEX.test(date)) {
    return null;
  }

  const [year, month, day] = date.split('-');
  return year.substring(2) + month + day; // YYMMDD
}

function getGenderFromId(id: string): 'M' | 'F' {
  return parseInt(id[6], 10) >= 5 ? 'M' : 'F';
}

export const ZA_ID_SKIP_CODES = ['9999999999999', '0000000000000'] as const;

export function isZaIdSkipCode(id: string): boolean {
  return ZA_ID_SKIP_CODES.some((skipCode) => skipCode === id);
}

export function validateZaIdValue(passportNo: string, birthdate?: string, gender?: string): ZaIdErrorCode | null {
  // Must be exactly 13 digits
  if (!/^\d{13}$/.test(passportNo)) {
    return ZaIdErrorCode.INVALID_FORMAT;
  }

  if (isZaIdSkipCode(passportNo)) {
    return null;
  }

  if (birthdate) {
    const expected = formatBirthdate(birthdate);
    const actual = passportNo.slice(0, 6);

    // Ignore birthdate matching when date is missing or not normalized to YYYY-MM-DD.
    if (expected && expected !== actual) {
      return ZaIdErrorCode.BIRTHDATE_MISMATCH;
    }
  }

  if (gender) {
    const inferred = getGenderFromId(passportNo);
    if (inferred !== gender) {
      return ZaIdErrorCode.GENDER_MISMATCH;
    }
  }

  if (!isValidLuhn(passportNo)) {
    return ZaIdErrorCode.INVALID_CHECKSUM;
  }

  return null;
}

export function useZaIdValidator() {
  const validateZaId = useCallback(
    (passportNo: string, birthdate?: string, gender?: string) => validateZaIdValue(passportNo, birthdate, gender),
    []
  );

  return { validateZaId };
}
